import { Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * Expiry date mask directive that provides format-as-you-type behavior.
 * Formats input as MM/YY with automatic slash insertion.
 *
 * Usage:
 * ```html
 * <input appExpiryMask [(ngModel)]="expiryDate" />
 * ```
 *
 * The ngModel will contain the formatted value (MM/YY).
 */
@Directive({
  selector: '[appExpiryMask]',
  standalone: true,
  host: {
    inputmode: 'numeric',
    autocomplete: 'cc-exp',
    maxlength: '5',
  },
})
export class ExpiryMaskDirective {
  private readonly _el = inject(ElementRef<HTMLInputElement>);

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;

    // Extract only digits, max 4
    let rawDigits = oldValue.replace(/\D/g, '').slice(0, 4);

    // Handle month input - auto-prefix with 0 if first digit > 1
    if (rawDigits.length === 1 && parseInt(rawDigits, 10) > 1) {
      rawDigits = '0' + rawDigits;
    }

    // Validate month range (01-12)
    if (rawDigits.length >= 2) {
      const month = parseInt(rawDigits.slice(0, 2), 10);
      if (month === 0) {
        rawDigits = '01' + rawDigits.slice(2);
      } else if (month > 12) {
        rawDigits = '12' + rawDigits.slice(2);
      }
    }

    // Format as MM/YY
    const formatted = this.formatExpiry(rawDigits);

    // Only update if the formatted value is different from current
    if (input.value !== formatted) {
      // Calculate new cursor position before updating value
      const newCursorPosition = this.calculateCursorPosition(oldValue, formatted, cursorPosition);

      // Update input value with formatted version
      input.value = formatted;

      // Restore cursor position
      requestAnimationFrame(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      });
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    const isCtrlCmd = event.ctrlKey || event.metaKey;
    const allowedWithCtrl = ['a', 'c', 'v', 'x'];
    // Allow: home, end, left, right arrows
    const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (
      allowedKeys.includes(event.key) ||
      navigationKeys.includes(event.key) ||
      (isCtrlCmd && allowedWithCtrl.includes(event.key.toLowerCase()))
    ) {
      return;
    }

    // Allow numbers only
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  private formatExpiry(digits: string): string {
    if (digits.length === 0) {
      return '';
    }

    if (digits.length <= 2) {
      return digits;
    }

    return digits.slice(0, 2) + '/' + digits.slice(2, 4);
  }

  private calculateCursorPosition(oldValue: string, newValue: string, oldPosition: number): number {
    // Count digits before cursor in old value
    const digitsBeforeCursor = oldValue.slice(0, oldPosition).replace(/\D/g, '').length;

    // Find position in new value that has the same number of digits before it
    let digitCount = 0;
    for (let i = 0; i < newValue.length; i++) {
      if (/\d/.test(newValue[i])) {
        digitCount++;
      }
      if (digitCount === digitsBeforeCursor) {
        // Position after the last matched digit
        return i + 1;
      }
    }

    return newValue.length;
  }
}
